<template>
  <v-container>
    <v-layout>
      <v-flex class="grey--text text--darken-3">
        <v-icon left>book</v-icon>
        <h1 class="all-caps mb-4 d-inline-block">
          Resources
        </h1>
      </v-flex>

      <div class="text-xs-right" v-if="user.managerOrHigher">
        <v-flex>
          <v-dialog v-model="newCategoryDialog" max-width="290">
            <v-btn slot="activator" class="green white--text">
              <v-icon left>folder</v-icon>
              Create Category
            </v-btn>
            <v-card>
              <v-card-title class="headline">Create Category</v-card-title>
              <v-card-text>
                <v-text-field v-model="newCategoryName" label="Category Name">
                </v-text-field>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="grey" flat @click="newCategoryDialog = false"
                  >Nevermind</v-btn
                >
                <v-btn color="success" @click.prevent="saveCategory()">
                  Create
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-flex>

        <v-flex>
          <v-dialog
            v-model="newResourceDialog"
            max-width="600"
            :disabled="resourceCategories.length == 0"
          >
            <v-btn
              :disabled="resourceCategories.length == 0"
              slot="activator"
              class="green white--text"
            >
              <v-icon left>add</v-icon>
              Add Resource
            </v-btn>
            <v-card>
              <v-card-title class="headline">Add Resource</v-card-title>

              <v-card-text>
                <p>
                  Select roles that can access resource:<br />
                  <small>(Superadmin users can access all resources)</small>
                </p>
                <v-layout row wrap>
                  <v-checkbox
                    v-model="selectedUserRole"
                    label="Back of House"
                    value="boh"
                  ></v-checkbox>
                  <v-checkbox
                    v-model="selectedUserRole"
                    label="Manager"
                    value="manager"
                  ></v-checkbox>
                  <v-checkbox
                    v-model="selectedUserRole"
                    label="Client"
                    value="client"
                  ></v-checkbox>
                  <v-checkbox
                    v-model="selectedUserRole"
                    label="Employee"
                    value="employee"
                  ></v-checkbox>
                </v-layout>

                <v-select
                  multiple
                  chips
                  deletable-chips
                  v-model="selectedSegmentIds"
                  :items="allSegments"
                  item-value="id"
                  item-text="name"
                  label="Geography (leave blank for everyone)"
                ></v-select>
              </v-card-text>

              <v-card-text>
                Add within category:

                <v-autocomplete
                  :items="resourceCategories"
                  item-text="name"
                  item-value="id"
                  v-model="resourceCategoryId"
                />

                <div v-show="resourceCategoryId">
                  <v-text-field v-model="resourceName" label="Resource Title">
                  </v-text-field>

                  <v-flex>
                    <v-menu
                      ref="effectiveDateMenu"
                      v-model="effectiveDateMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      lazy
                      transition="scale-transition"
                      offset-y
                      full-width
                      max-width="290px"
                      min-width="290px"
                    >
                      <template slot="activator" slot-scope="{ on }">
                        <v-text-field
                          v-model="effectiveDate"
                          label="Effective Date"
                          prepend-icon="event"
                          readonly
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="effectiveDate"
                        @input="effectiveDateMenu = false"
                        no-title
                        scrollable
                      >
                        <v-spacer></v-spacer>
                      </v-date-picker>
                    </v-menu>
                  </v-flex>

                  <v-textarea
                    v-model="resourceDescription"
                    label="Resource Description"
                  >
                  </v-textarea>

                  <v-radio-group v-model="resourceType">
                    <v-radio label="File Attachment" value="file"></v-radio>
                    <v-radio label="Video Embed" value="video"></v-radio>
                  </v-radio-group>

                  <div v-if="resourceType == 'file'">
                    <Uploader
                      accept="image/jpeg,image/gif,image/png,application/pdf,image/x-eps"
                      @presigned="resource_file_data = $event"
                    >
                    </Uploader>
                  </div>
                  <div v-else-if="resourceType == 'video'">
                    <v-textarea v-model="embedCode" label="Embed Code">
                    </v-textarea>
                  </div>
                </div>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="grey" flat @click="newResourceDialog = false"
                  >Nevermind</v-btn
                >
                <v-btn
                  v-if="resourceType == 'file'"
                  color="success"
                  @click.prevent="saveResource()"
                  :disabled="!presignedPresent || !resourceName"
                  v-show="chosenFilename"
                  :loading="!presignedPresent || apiCallPending"
                >
                  Create
                </v-btn>
                <v-btn
                  v-if="resourceType == 'video'"
                  color="success"
                  @click.prevent="saveResource()"
                  :disabled="!embedCode || !resourceName"
                  v-show="embedCode"
                  :loading="apiCallPending"
                >
                  Create
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-flex>
      </div>
    </v-layout>

    <v-layout v-if="reorderPending">
      <v-progress-linear indeterminate />
    </v-layout>
    <v-layout v-else column>
      <v-layout column v-for="category in resourceCategories" class="mb-5">
        <v-flex v-if="anyVisibleResourcesInCategory(category)">

          <v-layout row v-if="renamingCategory.id === category.id">
            <v-flex shrink>
              <v-text-field
                :label="`Renaming '${ category.name }'...`"
                v-model="renamingCategory.name"
                :loading="renamingPending"
                :disabled="renamingPending"
                :success-message="renamingSuccess"
                :error-message="renamingError"
              />
            </v-flex>
            <v-flex>
              <v-btn
                icon
                max-width="300"
                class="green white--text"
                :disabled="renamingPending"
                @click="doCategoryRename(category, renamingCategory)"
              ><v-icon>check</v-icon>
              </v-btn>

              <v-btn
                icon
                max-width="300"
                class="grey white--text"
                :disabled="renamingPending"
                @click="renamingCategory = {}"
              ><v-icon>cancel</v-icon>
              </v-btn>
            </v-flex>
          </v-layout>

          <v-layout v-else>
            <h2 class="display-1 grey--text mb-4">
              {{ category.name }}
              <v-btn
                v-if="canRenameCategory(category)"
                icon
                max-width="300"
                class="blue white--text"
                @click="setRenamingCategory(category)"
              >
                <v-icon>edit</v-icon>
              </v-btn>

              <v-btn
                v-if="canDeleteCategory(category)"
                icon
                max-width="300"
                class="grey white--text"
                @click="moveCategoryHigher(category)"
              >
                <v-icon>arrow_upward</v-icon>
              </v-btn>

              <v-btn
                v-if="canDeleteCategory(category)"
                icon
                max-width="300"
                class="grey white--text"
                @click="moveCategoryLower(category)"
              >
                <v-icon>arrow_downward</v-icon>
              </v-btn>

              <v-btn
                v-if="canDeleteCategory(category)"
                icon
                max-width="300"
                class="red white--text"
                @click="setDeletingCategory(category)"
              >
                <v-icon>delete</v-icon>
              </v-btn>
            </h2>
          </v-layout>
          <v-layout
            row
            justify-space-between
            v-for="resource in category.resources"
          >
            <v-flex shrink>
              <h3>
                {{ resource.title }} ({{ resource.effective_date | moment("MMMM YYYY") }})
              </h3>
              <v-chip
                small
                disabled
                v-if="user.managerOrHigher"
                v-for="(segName, si) in resource.segment_names || []"
                :key="`seg-${resource.id}-${si}`"
              >{{ segName }}</v-chip>
              <p class="grey--text">{{ resource.description }}</p>
            </v-flex>

            <v-flex shrink text-xs-right>
              <v-layout row>
                <v-btn
                  color="error"
                  v-if="canEdit(resource)"
                  @click="setDeletingResource(resource)"
                >
                  delete
                </v-btn>

                <v-btn
                  color="primary"
                  v-if="canEdit(resource)"
                  @click="setEditingResource(resource)"
                >
                  edit
                </v-btn>

                <div v-if="resource.file_url">
                  <a
                    class="download-button"
                    :href="resource.file_url"
                    target="_blank"
                  >
                    <v-btn block>
                      <v-icon left>description</v-icon>
                      Download
                    </v-btn>
                  </a>
                </div>

                <div v-else>
                  <v-btn @click="openModel(resource)">
                    <v-icon left>play_circle_outline</v-icon>
                    Watch
                  </v-btn>
                </div>
              </v-layout>
            </v-flex>
          </v-layout>
          <v-divider class="mt-4" />
        </v-flex>
      </v-layout>
    </v-layout>

    <v-dialog v-model="deleteCategoryModal" max-width="400">
      <v-card>
        <v-card-text>
          <h2 class="headline">Are you sure?</h2>
          <p></p>
          <p class="body">
            This will permanently '{{ deletingCategory.name }}'.
          </p>
          <p class="body-2">
            If there are any resources within this category, they will be
            deleted as well.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" flat small @click="deleteCategoryModal = false"
            >Nevermind</v-btn
          >
          <v-btn
            color="error"
            small
            @click="destroyCateogry(deletingCategory); deleteCategoryModal = false"
          >
            Yes, Delete it
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="watchDialog">
      <v-card>
        <v-card-title
          ><h2 class="headline">{{ watchItem.title }}</h2></v-card-title
        >
        <v-card-text>
          <div class="resource-video-embed" v-html="watchItem.embed_code"></div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteResourceDialog" max-width="400">
      <v-card>
        <v-card-text>
          <h2 class="headline">Are you sure?</h2>
          <p class="body">
            This will permanently delete '{{ deletingResource.title }}'.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" flat small @click="deleteResourceDialog = false"
            >Nevermind</v-btn
          >
          <v-btn color="error" small @click="destroyResource(deletingResource)">
            Yes, Delete it
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-if="editingResource"
      v-model="editResourceDialog"
      max-width="400"
    >
      <v-card>
        <v-card-title class="headline"
          >Editing '{{ editingResource.originalTitle }}'</v-card-title
        >

        <v-card-text>
          <p>
            Select roles that can access resource:<br />
            <small>(Superadmin users can access all resources)</small>
          </p>
          <v-layout row wrap>
            <v-checkbox
              v-model="editingResource.role_access_level"
              label="Back of House"
              value="boh"
            ></v-checkbox>
            <v-checkbox
              v-model="editingResource.role_access_level"
              label="Manager"
              value="manager"
            ></v-checkbox>
            <v-checkbox
              v-model="editingResource.role_access_level"
              label="Client"
              value="client"
            ></v-checkbox>
            <v-checkbox
              v-model="editingResource.role_access_level"
              label="Employee"
              value="employee"
            ></v-checkbox>
          </v-layout>

          <v-select
            multiple
            chips
            deletable-chips
            v-model="editingResource.segment_ids"
            :items="allSegments"
            item-value="id"
            item-text="name"
            label="Geography (leave blank for everyone)"
          ></v-select>
        </v-card-text>

        <v-card-text>
          <v-autocomplete
            label="New Category"
            :items="resourceCategories"
            item-text="name"
            item-value="id"
            v-model="editingResource.resource_category_id"
          />

          <div>
            <v-text-field
              v-model="editingResource.title"
              label="New Resource Title"
            >
            </v-text-field>

            <v-flex>
              <v-menu
                ref="editingEffectiveDateMenu"
                v-model="editingEffectiveDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                max-width="290px"
                min-width="290px"
              >
                <template slot="activator" slot-scope="{ on }">
                  <v-text-field
                    v-model="editingResource.effective_date"
                    label="New Effective Date"
                    prepend-icon="event"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="editingResource.effective_date"
                  @input="editingEffectiveDateMenu = false"
                  no-title
                  scrollable
                >
                  <v-spacer></v-spacer>
                </v-date-picker>
              </v-menu>
            </v-flex>

            <v-textarea
              v-model="editingResource.description"
              label="New Resource Description"
            >
            </v-textarea>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" flat @click="editResourceDialog = false"
            >Nevermind</v-btn
          >
          <v-btn
            color="success"
            @click.prevent="updateResource(editingResource)"
            :disabled="apiCallPending"
            :loading="apiCallPending"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
  import {mapGetters, mapState} from 'vuex';
  import Uploader from '@/components/hr/uploader.vue';
  import ImagePreview from '@/components/hr/image-preview.vue';
  import resource from '@/api/resource';
  import collectionsApi from '@/api/collections';

  export default {
    components: {
      Uploader,
      ImagePreview,
    },
    created() {
      this.$store.dispatch('resources/getAll');
      this.$store.dispatch('resources/getRoots');
      collectionsApi.getGeotagSegments({}, segments => {
        this.allSegments = segments.map(s => ({
          id: s.id,
          name: s.segment_type === 'region' ? `${s.name} (region)` : s.name,
        }));
      });
    },
    watch: {
      resourceType: 'resetTypeFields',
      watchDialog: 'killVideo',
    },
    props: [],
    computed: {
      ...mapState('userAuth', ['user']),
      ...mapState('resources', ['resources', 'resourceCategories']),
      presignedPresent: function() {
        if (this.resource_file_data && this.resource_file_data.id) {
          return true;
        } else {
          return false;
        }
      },
      chosenFilename: function() {
        return this.$store.state.uploader.fileName;
      },
      // Drives the category rename/delete buttons below. Computed rather than set
      // once in created(): this route mounts without an index-wrapper, so the user
      // can land in the store after this component exists.
      currentUserRole: function() {
        if (
          this.user.bohOrHigher &&
          this.user.managerOrHigher &&
          this.user.clientOrHigher == true
        ) {
          return 'boh';
        } else if (
          this.user.managerOrHigher &&
          this.user.clientOrHigher == true
        ) {
          return 'manager';
        } else if (this.user.clientOrHigher == true) {
          return 'client';
        }
        return 'employee';
      },
    },
    methods: {
      createdByViewer: function(resource) {
        if (
          resource.created_by_user_id &&
          resource.created_by_user_id == this.user.id
        ) {
          return true;
        }
        return false;
      },

      // VisibleResources (app/query_objects/visible_resources.rb) is the single
      // authority on who may see a resource, and every endpoint feeding this screen
      // filters through it, so anything still in category.resources is visible.
      anyVisibleResourcesInCategory(category) {
        return (category.resources || []).length > 0;
      },

      canDeleteCategory(category) {
        return this.currentUserRole == 'boh' || this.currentUserRole == 'superadmin';
      },

      canRenameCategory(category) {
        return this.currentUserRole == 'boh' || this.currentUserRole == 'superadmin';
      },

      canEdit: function(resource) {
        if (this.createdByViewer(resource) || this.user.role == 'superadmin') {
          return true;
        }
        return false;
      },

      setDeletingResource: function(resource) {
        this.deleteResourceDialog = true;
        this.deletingResource = resource;
      },

      destroyResource: function(resource) {
        this.$store.dispatch('resources/destroy', resource.id);
        this.deleteResourceDialog = false;
      },

      setDeletingCategory: function(category) {
        this.deleteCategoryModal = true;
        this.deletingCategory = category;
      },

      destroyCateogry: function(category) {
        this.$store.dispatch('resources/destroyCategory', category.id);
        this.deleteCategoryModal = false;
      },

      setRenamingCategory: function(category) {
        this.renamingCategory = Object.assign({}, category);
      },

      doCategoryRename: function(originalCat, newCat) {
        this.renamingPending = true
        this.$store
          .dispatch('resources/updateCategory', newCat)
          .then(res => {
            this.renamingSuccess = ["Success!"]
          })
          .catch(e => {
            this.renamingError = ["Something went wrong"]
          })
          .finally(res => {
            this.renamingCategory = {};
            this.renamingPending = false;
          })
      },

      moveCategoryHigher: function(category) {
        this.reorderPending = true;
        this.$store
          .dispatch('resources/moveCategoryHigher', category)
          .finally(res => {
            this.reorderPending = false;
          });
      },

      moveCategoryLower: function(category) {
        this.reorderPending = true;
        this.$store
          .dispatch('resources/moveCategoryLower', category)
          .finally(res => {
            this.reorderPending = false;
          });
      },

      setEditingResource: function(resource) {
        this.editResourceDialog = true;
        this.editingResource = Object.assign({}, resource);
        this.editingResource['originalTitle'] = resource.title;

        // create proper js array out of json:
        const rolesArray = JSON.parse(resource.role_access_level);
        this.editingResource.role_access_level = rolesArray;
      },

      updateResource: function(updatedResource) {
        this.apiCallPending = true;
        this.$store
          .dispatch('resources/update', updatedResource)
          .finally(res => {
            this.editResourceDialog = false;
            this.editingResource = null;
            this.apiCallPending = false;
          });
      },

      killVideo(value) {
        if (value == false) {
          this.watchItem = {};
        }
      },
      openModel(item) {
        this.watchDialog = true;
        this.watchItem = item;
      },
      resetTypeFields() {
        this.embedCode = '';
        this.resource_file_data = null;
      },
      saveCategory() {
        this.apiCallPending = true;
        this.$store
          .dispatch('resources/createCategory', {
            name: this.newCategoryName,
          })
          .finally(res => {
            this.apiCallPending = false;
            this.newCategoryDialog = false;
          });
      },
      saveResource() {
        this.apiCallPending = true;
        this.$store
          .dispatch('resources/create', {
            resource_file_data: this.resource_file_data,
            title: this.resourceName,
            description: this.resourceDescription,
            effective_date: this.effectiveDate,
            resource_category_id: this.resourceCategoryId,
            embed_code: this.embedCode,
            role_access_level: this.selectedUserRole,
            segment_ids: this.selectedSegmentIds,
            created_by_user_id: this.user.id,
          })
          .finally(res => {
            this.$store.commit('uploader/setFilename', '');
            this.apiCallPending = false;
            this.newResourceDialog = false;
            this.resourceName = '';
            this.resource_file_data = null;
            this.effectiveDate = new Date().toISOString().substr(0, 10);
            this.embedCode = '';
            // Must stay an ARRAY. Vuetify's checkbox only does array semantics
            // while the value it is bound to IS an array (`multiple` defaults to
            // null - selectable.js:39), so resetting this to '' turned the role
            // checkboxes into a single-select that handed back a bare string.
            this.selectedUserRole = [];
            this.selectedSegmentIds = [];
          });
      },
    },
    data: function() {
      return {
        resourceType: 'file',
        apiCallPending: false,
        newResourceDialog: false,
        newCategoryDialog: false,
        deleteResourceDialog: false,
        editResourceDialog: false,
        deleteCategoryModal: false,
        deletingCategory: {},
        renamingCategory: {},
        renamingSuccess: [],
        renamingError: [],
        renamingPending: false,
        reorderPending: false,
        watchDialog: false,
        watchItem: {},
        resource_file_data: null,
        embedCode: '',
        resourceName: '',
        resourceDescription: '',
        editingResource: null,
        deletingResource: {},
        effectiveDate: new Date().toISOString().substr(0, 10),
        effectiveDateMenu: false,
        editingEffectiveDateMenu: false,
        resourceCategoryId: null,
        newCategoryName: '',
        topLevel: true,
        selectedUserRole: [],
        userRoles: ['employee', 'client', 'manager', 'boh'],
        allSegments: [],
        selectedSegmentIds: [],
      };
    },
  };
</script>

<style scoped>
  .download-button {
    text-decoration: none;
  }
</style>

<style>
  #resources-tree .v-treeview-node__root {
    align-items: flex-end !important;
  }

  #resources-tree .v-treeview-node__content {
    display: block !important;
    margin-bottom: 21px !important;
  }

  #resources-tree .v-treeview-node--leaf {
    margin-left: 44px !important;
  }

  #resources-tree .v-treeview-node__toggle {
    font-size: 44px;
  }

  .resource-video-embed iframe {
    display: block;
    width: 100%;
    height: 75vh;
  }
</style>
